import { Controller } from "@hotwired/stimulus";
import { findRollableNotations } from "../utils/dice";

// Connects to data-controller="dice-notation"
//
// Scans description prose for dice notation (1d6, 2d4+2, 1d6+2d6) and wraps each
// match in a clickable span that rolls it through the page's roller (page-dice
// reads its params). A local-roll shortcut — it rolls the literal notation as
// written, NOT any per-level scaling. No rolling code here.
const SKIP_TAGS = new Set(["A", "BUTTON", "H1", "H2", "H3", "H4", "H5", "H6", "SCRIPT", "STYLE"]);

export default class extends Controller {
	static values = { roll: String, rollSuccess: String };

	connect() {
		if (this.element.dataset.diceNotationProcessed) return;
		this.element.dataset.diceNotationProcessed = "true";

		const walker = document.createTreeWalker(this.element, NodeFilter.SHOW_TEXT, {
			acceptNode: (node) => this.#accept(node),
		});

		const textNodes = [];
		let node = walker.nextNode();
		while (node) {
			textNodes.push(node);
			node = walker.nextNode();
		}
		for (const textNode of textNodes) this.#wrap(textNode);
	}

	// Only text that holds a "dN" and is not already inside a link/heading/roll button.
	#accept(node) {
		if (!node.nodeValue || !/d\d/i.test(node.nodeValue)) return NodeFilter.FILTER_REJECT;

		for (let el = node.parentElement; el && el !== this.element; el = el.parentElement) {
			if (SKIP_TAGS.has(el.tagName) || el.hasAttribute("data-dice-bar-instruction-param")) {
				return NodeFilter.FILTER_REJECT;
			}
		}
		return NodeFilter.FILTER_ACCEPT;
	}

	#wrap(textNode) {
		const text = textNode.nodeValue;
		const matches = findRollableNotations(text);
		if (matches.length === 0) return;

		const fragment = document.createDocumentFragment();
		let lastIndex = 0;
		for (const match of matches) {
			if (match.index > lastIndex) fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
			fragment.appendChild(this.#button(match));
			lastIndex = match.index + match.text.length;
		}
		if (lastIndex < text.length) fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
		textNode.parentNode.replaceChild(fragment, textNode);
	}

	// The templates are the server's, in the viewer's language; a page without them gets the bare notation.
	#title(instruction, success) {
		const template = success ? this.rollSuccessValue : this.rollValue;
		return template ? template.replace("%{notation}", instruction).replace("%{success}", success) : instruction;
	}

	#button({ text, instruction, success }) {
		const span = document.createElement("span");
		span.className = "odo-rollable";
		span.textContent = text;
		span.title = this.#title(instruction, success);
		span.setAttribute("role", "button");
		span.setAttribute("tabindex", "0");
		span.setAttribute("aria-label", span.title);
		span.dataset.diceBarInstructionParam = instruction;
		if (success) span.dataset.diceBarSuccessParam = success;
		return span;
	}
}
